import _ from "lodash";
import {FeatureCollection, GeoJsonProperties, MultiPolygon, Polygon, BBox, Feature, Position} from "geojson";
import * as turf from "@turf/turf";
import booleanIntersects from "@turf/boolean-intersects";
import {LngLatBounds} from "mapbox-gl";

export const createBoundingBox = (fieldPolygons: Polygon[] | Polygon | MultiPolygon): BBox => {
    if (Array.isArray(fieldPolygons)) {
        const fc = turf.featureCollection(fieldPolygons.map((polygon) => turf.feature(polygon)));
        const combined = turf.combine(fc);
        const bbox = turf.bbox(combined);
        return bbox;
    } else if (fieldPolygons.type === "Polygon" || fieldPolygons.type === "MultiPolygon") {
        return turf.bbox(fieldPolygons);
    } else {
        console.error("Unexpected type for for Polygon. Trying best to create a bounding box. ");
        return turf.bbox(fieldPolygons);
    }
};

/**
 * Converts bbox format of turf [ minX, minY, maxX, maxY] to mapbox [(topLeft, topRight, bottomRight, bottomLeft)]
 */
export const bboxToMapbox = function (bb: number[]): number[][] {
    const result = [
        [bb[0], bb[3]],
        [bb[2], bb[3]],
        [bb[2], bb[1]],
        [bb[0], bb[1]],
    ];
    return result;
};

export const getImageRectFromGeometry = function (fieldPolygons: Polygon[] | Polygon | MultiPolygon): number[][] {
    return bboxToMapbox(createBoundingBox(fieldPolygons));
};

export const createFeatureCollection = <T = unknown>(
    items: ({id?: string; name?: string; color?: string; geometry: MultiPolygon | Polygon | null} & T)[],
): FeatureCollection<MultiPolygon | Polygon, GeoJsonProperties> => {
    const fieldsGeo = items
        .filter((item) => item.geometry !== null)
        .map((item) => {
            const feature = turf.feature(item.geometry);
            if (!feature.properties) {
                feature.properties = {};
            }
            feature.properties = {...feature.properties, ...item};
            return feature;
        });

    return turf.featureCollection(fieldsGeo);
};

export const combineFeaturesIntoMultiPolygon = (features: Feature[]) => {
    const polygons = features.map((feature) => {
        const geometry = feature.geometry as Polygon | MultiPolygon;
        return geometry.coordinates;
    });
    const multiPolygon = turf.multiPolygon(polygons as Position[][][]);
    return multiPolygon;
};

export const calcAreaHaGeometry = (geometry: MultiPolygon | null): number => {
    if (geometry) {
        const areaSqMeters = turf.area(geometry);
        return _.round(areaSqMeters / 10000, 2);
    }
    return 0;
};

export const calcAreaHa = (field: {geometry: MultiPolygon | null}): number => {
    if (field.geometry) {
        return calcAreaHaGeometry(field.geometry);
    }
    return 0;
};

export const toLngLat = (position: Position): {lng: number; lat: number} => {
    return {
        lng: position[0],
        lat: position[1],
    };
};

export const fieldInsideMap = (geometry: MultiPolygon | Polygon, bounds: LngLatBounds): boolean => {
    const expandedBounds = bounds.toArray();
    const [minLng, minLat] = expandedBounds[0];
    const [maxLng, maxLat] = expandedBounds[1];

    const bufferFactor = 0.75;

    const expandedMinLng = minLng - (maxLng - minLng) * bufferFactor;
    const expandedMinLat = minLat - (maxLat - minLat) * bufferFactor;
    const expandedMaxLng = maxLng + (maxLng - minLng) * bufferFactor;
    const expandedMaxLat = maxLat + (maxLat - minLat) * bufferFactor;

    const expandedBbox: [number, number, number, number] = [
        expandedMinLng,
        expandedMinLat,
        expandedMaxLng,
        expandedMaxLat,
    ];

    const expandedPolygon = turf.bboxPolygon(expandedBbox).geometry;
    const fieldBbox = createBoundingBox(geometry);
    const fieldPolygon = turf.bboxPolygon(fieldBbox).geometry;

    return booleanIntersects(expandedPolygon, fieldPolygon);
};
export const toPolygon = (bounds: LngLatBounds): Polygon => {
    const ar = bounds.toArray();

    const bbox: [number, number, number, number] = [ar[0][0], ar[0][1], ar[1][0], ar[1][1]];
    const pol = turf.bboxPolygon(bbox);
    return pol.geometry;
};

export const getCenter = (geometry: MultiPolygon): {lng: number; lat: number} => {
    const feature = turf.feature(geometry);
    const center = turf.center(feature);

    return toLngLat(center.geometry.coordinates);
};
